<template>
  <span
    v-bind="wrapperAttrs"
    class="base-img"
    :class="attrs.class"
    :style="wrapperStyle"
  >
    <NuxtPicture
      :src="src"
      :alt="alt"
      :sizes="sizes"
      :fit="fit"
      :width="numericWidth"
      :height="numericHeight"
      :img-attrs="imgAttrs"
    />
  </span>
</template>

<script setup lang="ts">
import type { CSSProperties, ImgHTMLAttributes } from 'vue'

defineOptions({
  name: 'BaseImg',
  inheritAttrs: false,
})

const attrs = useAttrs()
const wrapperAttrs = computed(() => {
  const { class: _class, style: _style, ...rest } = attrs
  return rest
})

const props = defineProps({
  src: { type: String, required: true },
  alt: { type: String, default: '' },
  sizes: {
    type: String,
    default: 'xs:100vw sm:100vw md:100vw lg:100vw xl:100vw',
  },
  width: { type: [Number, String], default: undefined },
  height: { type: [Number, String], default: undefined },
  maxWidth: { type: [Number, String], default: undefined },
  minHeight: { type: [Number, String], default: undefined },
  cover: Boolean,
  contain: Boolean,
})

function toCssSize(value: number | string | undefined) {
  if (value === undefined || value === null || value === '') {
    return undefined
  }

  if (typeof value === 'string' && /^\d+(\.\d+)?$/.test(value)) {
    return `${value}px`
  }

  return typeof value === 'number' ? `${value}px` : value
}

function toNumericSize(value: number | string | undefined) {
  if (typeof value === 'number') {
    return value
  }

  if (typeof value === 'string' && /^\d+$/.test(value)) {
    return Number(value)
  }

  return undefined
}

const fit = computed(() => {
  if (props.contain) {
    return 'contain'
  }

  return props.cover ? 'cover' : 'cover'
})

const wrapperStyle = computed(() => ({
  width: toCssSize(props.width) || '100%',
  maxWidth: toCssSize(props.maxWidth),
  minHeight: toCssSize(props.minHeight),
  height: toCssSize(props.height),
}))

const imgAttrs = computed(
  () =>
    ({
      class: 'base-img__image',
      style: {
        objectFit: fit.value,
        width: '100%',
        height: '100%',
      } satisfies CSSProperties,
    }) satisfies ImgHTMLAttributes,
)

const numericWidth = computed(() => toNumericSize(props.width))
const numericHeight = computed(() => toNumericSize(props.height))
</script>

<style scoped>
.base-img {
  display: block;
  line-height: 0;
}

.base-img :deep(picture) {
  display: block;
}

.base-img :deep(img) {
  display: block;
  max-width: 100%;
}
</style>
